/**
 * Add-to-cart analytics helpers.
 *
 * Moved from templates/components/scripts/atc-analytics.html (Phase 3 refactor).
 * Handlebars dependency (storefront API token) is read at call-time from window.BC_CONTEXT
 * which is populated by init-globals.html before the webpack bundle loads.
 */

export function parseBoolean(value) {
    if (typeof value === 'string') {
        const trimmedValue = value.trim().toLowerCase();
        if (trimmedValue === 'true') {
            return true;
        }
        if (trimmedValue === 'false') {
            return false;
        }
    }
    return Boolean(value);
}

// source: optional { recommender, recommenderName } (custom/event/recommender-context.js),
// copied onto each event so recommender adds match their add_to_cart_start click.
export async function getAddToCartData(cartItemResponse, eventProductId, quantityValue, skuText, source) {
  console.debug("cartItemResponse:", cartItemResponse);

  const pageCategories = typeof window.breadcrumbToCategories === "function" ? window.breadcrumbToCategories() : undefined;
  const eventCategoryMap = {
    product: "product_details",
    default: "home_page",
    category: "product_listing",
  };
  const pageType = typeof window.getPageType === "function" ? window.getPageType() : (window.BC_CONTEXT?.pageType || '');
  const eventCategory = eventCategoryMap[pageType] || "recommender";

  // Make failSent visible to catch block
  let failSent = false;

  const newEvent = {
    loadNumber: 1,
    scriptLoaded: false,
    interactionsCompleted: false,
    errorMessage: {
      eventName: "add_to_cart",
      eventMessage: "",
      pageType,
      timeStamp: new Date().toISOString(),
    },
  };

  // De-Tealium: utag vendor removed — hasUtag is now a no-op (always false).
  const hasUtag = () => false;

  // Helper: normalize items from various response shapes
  const extractItems = (resp) => {
    const data = resp?.data;
    const li = data?.line_items;

    if (!li) return [];

    // Array of individual items (e.g., line_items: [{...}, {...}])
    if (Array.isArray(li)) {
      console.debug("Normalized items from data.line_items (array)");
      return li;
    }

    // Grouped object (e.g., { physical_items: [...], digital_items: [...] })
    if (li.physical_items || li.digital_items || li.custom_items || li.gift_certificates) {
      console.debug("Normalized items from data.line_items (grouped object)", Object.keys(li));
      return [
        ...(li.physical_items ?? []),
        ...(li.digital_items ?? []),
        ...(li.custom_items ?? []),
        ...(li.gift_certificates ?? []),
      ];
    }

    // Single cart_item object
    console.debug("Normalized items from data.line_items (single object)");
    return [li];
  };

  // Helper: map any item shape to the analytics payload you expect
  const mapItemToEventObject = (item) => {
    const productId = item.product_id ?? item.productId ?? eventProductId ?? "";
    const quantity = item.quantity ?? quantityValue ?? 1;
    const price = item.purchase_price ?? item.price?.value ?? item.price ?? "";
    const name = item.product_name ?? item.name ?? "";
    const sku = skuText ?? item.sku ?? "";
    const variantId = item.variant_id ?? item.variantId ?? "";
    const brand = item.brand_name ?? item.brand ?? "";

    return {
      tealium_event: "add_to_cart",
      event_action: "click",
      event_category: "add to cart",
      product_id: [productId],
      product_name: [name],
      product_price: [price],
      product_quantity: [quantity],
      product_sku: [sku],
      product_variant: [variantId],
      add_to_cart_type: "by sku",
      event_label: eventCategory,
      event_value: "add_to_cart",
      page_category: pageCategories ? pageCategories.categoryCurrent : undefined,
      page_subcategory: pageCategories ? pageCategories.subCategoryCurrent : undefined,
      product_brand: [brand],
      product_category: [pageCategories ? pageCategories.categoryCurrent : undefined],
      product_subcategory: [pageCategories ? pageCategories.subCategoryCurrent : undefined],
      ...source,
    };
  };

  try {
    const resp = cartItemResponse?.data;
    const backendError = resp?.Error;
    if (backendError != null) {
      newEvent.errorMessage.eventMessage =
        "No product details returned from getAddToCartProductDetails | " + String(backendError);
      console.warn("getAddToCartData backend error:", newEvent.errorMessage.eventMessage);
      if (hasUtag()) newEvent.scriptLoaded = true;
      return; // Early exit on backend error
    }

    const items = extractItems(cartItemResponse);

    if (!items.length) {
      newEvent.errorMessage.eventMessage += "No line items found in cart response|";
      console.warn("getAddToCartData: No line items found", resp?.line_items ?? resp);
      if (hasUtag()) newEvent.scriptLoaded = true;
      return;
    }

    for (const item of items) {
      if (hasUtag()) newEvent.scriptLoaded = true;

      const eventObject = mapItemToEventObject(item);

      try {
        // De-Tealium: utag.link vendor send removed; the onlineService dispatch
        // (queueEventFile) is the surviving analytics path.
        failSent = true;
        window.queueEventFile?.(newEvent, eventObject);

      } catch (err) {
        console.error("queueEventFile failed:", err, eventObject);
        newEvent.errorMessage.eventMessage += `queueEventFile failed: ${err?.message ?? err}|`;
        window.queueEventFile?.(newEvent, eventObject);
        failSent = true;
      }
    }
  } catch (error) {
    newEvent.errorMessage.eventMessage = `Error in getAddToCartData: ${error?.message ?? error}`;
    console.error("Error in getAddToCartData:", error, cartItemResponse);

    if (!failSent) {
      if (hasUtag()) newEvent.scriptLoaded = true;
      const emptyEvent = mapItemToEventObject({});
      setTimeout(() => {
        window.queueEventFile?.(newEvent, emptyEvent);
      }, 500);
    }
  }
}

export async function getAddToCartProductDetails(currentOrderProductIds) {
    const token = window.BC_CONTEXT?.storefrontToken || '';
    try {
        const response = await fetch('/graphql', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                query: `query GetOrderAllProductDetails {
                    site {
                        products(entityIds: [${currentOrderProductIds}]) {
                            edges {
                                node {
                                    sku
                                    name
                                    entityId
                                    brand {
                                        name
                                    }
                                    prices {
                                        price {
                                            formatted
                                        }
                                        basePrice {
                                            formatted
                                        }
                                    }
                                }
                            }
                            collectionInfo {
                                totalItems
                            }
                        }
                    }
                }`
            }),
        });

        if (!response.ok) {
            const message = `HTTP error! status: ${response.status}`;
            console.error('Error fetching product details:', message, response);
            throw new Error(message);
        }

        try {
            const result = await response.json();

            if (!result.data || !result.data.site || !result.data.site.products) {
                const message = 'Unexpected GraphQL response structure: Missing data.site.products';
                console.error('Error fetching product details:', message, result);
                throw new Error(message);
            }

            return result.data.site.products;
        } catch (jsonError) {
            console.error('Error parsing JSON response:', jsonError, response);
            throw new Error(`Error parsing JSON response: ${jsonError.message}`);
        }

    } catch (error) {
        console.error('Error fetching product details:', error);
        return null;
    }
}
